import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../config/firebase';
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';

const FUNCTIONS_URL = 'https://us-central1-datezy-production.cloudfunctions.net';

// Chatbot kullanıcısı tipi
interface ChatbotUser {
  id: string;
  botUserId: string;
  botEmail: string;
  botName: string;
  botGender: string;
  enabled: boolean;
  persona: {
    name: string;
    age: number;
    birthPlace: string;
    currentCity: string;
    livingWith: string;
    maritalStatus: string;
    jobStatus: string;
    profession: string;
  };
  maxUserMessages: number;
  useMinuteDelay: boolean;
  minReplyDelaySeconds: number;
  maxReplyDelaySeconds: number;
  minReplyDelayMinutes: number;
  maxReplyDelayMinutes: number;
  contextMessages: number;
  farewellMessage: string;
  followupMessage: string;
  totalResponses: number;
}

// Bot istatistikleri tipi
interface BotStats {
  processedResponses: number;
  processedGreetings: number;
  pendingResponses: number;
  pendingGreetings: number;
}

const ChatbotPage: React.FC = () => {
  const [chatbotUsers, setChatbotUsers] = useState<ChatbotUser[]>([]);
  const [stats, setStats] = useState<BotStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Yeni bot ekleme
  const [newBotEmail, setNewBotEmail] = useState('');
  const [addingBot, setAddingBot] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newBotSettings, setNewBotSettings] = useState({
    useMinuteDelay: false,
    minReplyDelaySeconds: 12,
    maxReplyDelaySeconds: 20,
    minReplyDelayMinutes: 30,
    maxReplyDelayMinutes: 60,
    maxUserMessages: 8,
    contextMessages: 20,
    persona: {
      name: '',
      age: 25,
      birthPlace: '',
      currentCity: '',
      livingWith: '',
      maritalStatus: 'Evlenmemiş',
      jobStatus: '',
      profession: ''
    }
  });

  // Bot düzenleme
  const [editingBot, setEditingBot] = useState<ChatbotUser | null>(null);

  // Bot mesajları
  const [viewingMessages, setViewingMessages] = useState<string | null>(null);
  const [botMessages, setBotMessages] = useState<any[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Eski sistem ayarları (geriye uyumluluk)
  const [oldSettings, setOldSettings] = useState<any>(null);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [savingOldSettings, setSavingOldSettings] = useState(false);

  // Chatbot kullanıcılarını yükle
  const loadChatbotUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${FUNCTIONS_URL}/getChatbotUsers`);
      const data = await response.json();
      
      if (data.success) {
        setChatbotUsers(data.chatbotUsers || []);
      }
    } catch (error) {
      console.error('Chatbot kullanıcıları yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Eski ayarları yükle
  const loadOldSettings = useCallback(async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'bot_settings', 'config'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setOldSettings(data);
        if (data.openaiApiKey) {
          setApiKeyInput(data.openaiApiKey.substring(0, 10) + '...');
        }
      }
    } catch (error) {
      console.error('Eski ayarlar yüklenemedi:', error);
    }
  }, []);

  // İstatistikleri yükle
  const loadStats = useCallback(async () => {
    try {
      const processedQueueSnapshot = await getDocs(
        query(collection(db, 'bot_queue'), where('processed', '==', true), orderBy('createdAt', 'desc'), limit(100))
      );
      const processedGreetingSnapshot = await getDocs(
        query(collection(db, 'bot_greeting_queue'), where('processed', '==', true), orderBy('createdAt', 'desc'), limit(100))
      );
      const pendingQueueSnapshot = await getDocs(
        query(collection(db, 'bot_queue'), where('processed', '==', false))
      );
      const pendingGreetingSnapshot = await getDocs(
        query(collection(db, 'bot_greeting_queue'), where('processed', '==', false))
      );

      setStats({
        processedResponses: processedQueueSnapshot.size,
        processedGreetings: processedGreetingSnapshot.size,
        pendingResponses: pendingQueueSnapshot.size,
        pendingGreetings: pendingGreetingSnapshot.size
      });
    } catch (error) {
      console.error('İstatistikler yüklenemedi:', error);
    }
  }, []);

  // Yeni chatbot ekle
  const addChatbotUser = async () => {
    if (!newBotEmail.trim()) {
      setMessage({ type: 'error', text: 'Lütfen email girin' });
      return;
    }

    try {
      setAddingBot(true);
      const response = await fetch(`${FUNCTIONS_URL}/addChatbotUser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          botEmail: newBotEmail.trim(),
          ...newBotSettings
        })
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: data.message });
        setNewBotEmail('');
        setShowAddForm(false);
        loadChatbotUsers();
      } else {
        setMessage({ type: 'error', text: data.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Bağlantı hatası' });
    } finally {
      setAddingBot(false);
    }
  };

  // Chatbot sil
  const removeChatbotUser = async (docId: string, botName: string) => {
    if (!window.confirm(`${botName} chatbot kullanıcısını silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      const response = await fetch(`${FUNCTIONS_URL}/removeChatbotUser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ docId })
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'Chatbot silindi' });
        loadChatbotUsers();
      } else {
        setMessage({ type: 'error', text: data.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Bağlantı hatası' });
    }
  };

  // Chatbot aç/kapat
  const toggleChatbotUser = async (docId: string, currentEnabled: boolean) => {
    try {
      const response = await fetch(`${FUNCTIONS_URL}/toggleChatbotUser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ docId, enabled: !currentEnabled })
      });

      const data = await response.json();

      if (data.success) {
        loadChatbotUsers();
      } else {
        setMessage({ type: 'error', text: data.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Bağlantı hatası' });
    }
  };

  // Cevaplanmamış mesajları tara
  const [scanningBot, setScanningBot] = useState<string | null>(null);
  const scanChatbotMessages = async (docId: string, botName: string, forceRescan: boolean = false) => {
    try {
      setScanningBot(docId);
      const response = await fetch(`${FUNCTIONS_URL}/scanChatbotUserMessages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ docId, forceRescan })
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: `${botName}: ${data.message}` });
        loadStats();
      } else {
        setMessage({ type: 'error', text: data.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Bağlantı hatası' });
    } finally {
      setScanningBot(null);
    }
  };

  // Bot mesajlarını yükle
  const loadBotMessages = async (botUserId: string, botName: string) => {
    if (viewingMessages === botUserId) {
      setViewingMessages(null);
      setBotMessages([]);
      return;
    }

    try {
      setLoadingMessages(true);
      setViewingMessages(botUserId);
      
      // bot_queue'dan son mesajları al
      const messagesSnapshot = await getDocs(
        query(
          collection(db, 'bot_queue'),
          where('botUserId', '==', botUserId),
          where('processed', '==', true),
          orderBy('createdAt', 'desc'),
          limit(50)
        )
      );

      const messages = messagesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));

      setBotMessages(messages);
    } catch (error) {
      console.error('Mesajlar yüklenemedi:', error);
      setMessage({ type: 'error', text: 'Mesajlar yüklenemedi' });
    } finally {
      setLoadingMessages(false);
    }
  };

  // Chatbot güncelle
  const updateChatbotUser = async () => {
    if (!editingBot) return;

    try {
      const response = await fetch(`${FUNCTIONS_URL}/updateChatbotUser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          docId: editingBot.id,
          persona: editingBot.persona,
          maxUserMessages: editingBot.maxUserMessages,
          useMinuteDelay: editingBot.useMinuteDelay,
          minReplyDelaySeconds: editingBot.minReplyDelaySeconds,
          maxReplyDelaySeconds: editingBot.maxReplyDelaySeconds,
          minReplyDelayMinutes: editingBot.minReplyDelayMinutes,
          maxReplyDelayMinutes: editingBot.maxReplyDelayMinutes,
          contextMessages: editingBot.contextMessages,
          farewellMessage: editingBot.farewellMessage,
          followupMessage: editingBot.followupMessage
        })
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'Chatbot güncellendi' });
        setEditingBot(null);
        loadChatbotUsers();
      } else {
        setMessage({ type: 'error', text: data.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Bağlantı hatası' });
    }
  };

  // Eski ayarları kaydet (API key için)
  const saveOldSettings = async () => {
    try {
      setSavingOldSettings(true);
      
      const dataToSave: any = {
        ...oldSettings,
        updatedAt: serverTimestamp()
      };

      if (apiKeyInput && !apiKeyInput.includes('...')) {
        dataToSave.openaiApiKey = apiKeyInput;
      }

      await setDoc(doc(db, 'bot_settings', 'config'), dataToSave, { merge: true });
      setMessage({ type: 'success', text: 'OpenAI API Key kaydedildi!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Kaydetme hatası' });
    } finally {
      setSavingOldSettings(false);
    }
  };

  useEffect(() => {
    loadChatbotUsers();
    loadOldSettings();
    loadStats();
    
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, [loadChatbotUsers, loadOldSettings, loadStats]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🤖</div>
        <p>Chatbot ayarları yükleniyor...</p>
      </div>
    );
  }

  const activeCount = chatbotUsers.filter(u => u.enabled).length;

  return (
    <div style={{ padding: '0' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        borderRadius: '16px',
        padding: '2rem',
        marginBottom: '1.5rem',
        color: 'white'
      }}>
        <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.8rem', fontWeight: '600' }}>
          🤖 Chatbot Yönetimi
        </h2>
        <p style={{ margin: 0, opacity: 0.9 }}>
          AI destekli otomatik sohbet botları - {activeCount} aktif / {chatbotUsers.length} toplam
        </p>
      </div>

      {/* Message */}
      {message && (
        <div style={{
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          backgroundColor: message.type === 'success' ? '#d1fae5' : '#fee2e2',
          color: message.type === 'success' ? '#065f46' : '#991b1b'
        }}>
          {message.type === 'success' ? '✅' : '❌'} {message.text}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>{stats.processedResponses}</div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Gönderilen Yanıt</div>
          </div>
          <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6' }}>{stats.processedGreetings}</div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Gönderilen Selamlama</div>
          </div>
          <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b' }}>{stats.pendingResponses}</div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Bekleyen Yanıt</div>
          </div>
          <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: activeCount > 0 ? '#10b981' : '#ef4444' }}>{activeCount}</div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Aktif Bot</div>
          </div>
        </div>
      )}

      {/* OpenAI API Key (Global) */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <h3 style={{ margin: '0 0 1rem 0' }}>🔑 OpenAI API Key (Tüm Botlar İçin)</h3>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <input
            type="password"
            value={apiKeyInput}
            onChange={(e) => setApiKeyInput(e.target.value)}
            placeholder="sk-..."
            style={{ flex: 1, padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '8px' }}
          />
          <button
            onClick={saveOldSettings}
            disabled={savingOldSettings}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: savingOldSettings ? '#9ca3af' : '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: savingOldSettings ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            {savingOldSettings ? '⏳' : '💾'} Kaydet
          </button>
        </div>
        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: '#666' }}>
          Bu API key tüm chatbot'lar tarafından kullanılır.
        </p>
      </div>

      {/* Chatbot Kullanıcıları */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0 }}>👥 Chatbot Kullanıcıları</h3>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: showAddForm ? '#ef4444' : '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            {showAddForm ? '❌ İptal' : '➕ Yeni Chatbot Ekle'}
          </button>
        </div>

        {/* Yeni Bot Ekleme Formu */}
        {showAddForm && (
          <div style={{ backgroundColor: '#f0fdf4', padding: '1.5rem', borderRadius: '12px', marginBottom: '1.5rem', border: '2px solid #86efac' }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#166534' }}>🤖 Yeni Chatbot Ekle</h4>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Kullanıcı Email</label>
              <input
                type="email"
                value={newBotEmail}
                onChange={(e) => setNewBotEmail(e.target.value)}
                placeholder="meltem@gmail.com"
                style={{ width: '100%', padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '8px', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>İsim</label>
                <input
                  type="text"
                  value={newBotSettings.persona.name}
                  onChange={(e) => setNewBotSettings(prev => ({ ...prev, persona: { ...prev.persona, name: e.target.value } }))}
                  placeholder="Meltem"
                  style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Yaş</label>
                <input
                  type="number"
                  value={newBotSettings.persona.age}
                  onChange={(e) => setNewBotSettings(prev => ({ ...prev, persona: { ...prev.persona, age: parseInt(e.target.value) } }))}
                  style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Doğum Yeri</label>
                <input
                  type="text"
                  value={newBotSettings.persona.birthPlace}
                  onChange={(e) => setNewBotSettings(prev => ({ ...prev, persona: { ...prev.persona, birthPlace: e.target.value } }))}
                  placeholder="İstanbul"
                  style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Yaşadığı Yer</label>
                <input
                  type="text"
                  value={newBotSettings.persona.currentCity}
                  onChange={(e) => setNewBotSettings(prev => ({ ...prev, persona: { ...prev.persona, currentCity: e.target.value } }))}
                  placeholder="İstanbul, Pendik"
                  style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Kiminle Yaşıyor</label>
                <input
                  type="text"
                  value={newBotSettings.persona.livingWith}
                  onChange={(e) => setNewBotSettings(prev => ({ ...prev, persona: { ...prev.persona, livingWith: e.target.value } }))}
                  placeholder="Abisiyle"
                  style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Meslek</label>
                <input
                  type="text"
                  value={newBotSettings.persona.profession}
                  onChange={(e) => setNewBotSettings(prev => ({ ...prev, persona: { ...prev.persona, profession: e.target.value } }))}
                  placeholder="Aşçı"
                  style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>İş Durumu</label>
                <input
                  type="text"
                  value={newBotSettings.persona.jobStatus}
                  onChange={(e) => setNewBotSettings(prev => ({ ...prev, persona: { ...prev.persona, jobStatus: e.target.value } }))}
                  placeholder="Restoranda çalışıyor"
                  style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Mesaj Limiti (0=sınırsız)</label>
                <input
                  type="number"
                  value={newBotSettings.maxUserMessages}
                  onChange={(e) => setNewBotSettings(prev => ({ ...prev, maxUserMessages: parseInt(e.target.value) }))}
                  min="0"
                  style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            {/* Gecikme Ayarları */}
            <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#fef3c7', borderRadius: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '1rem' }}>
                <input
                  type="checkbox"
                  checked={newBotSettings.useMinuteDelay}
                  onChange={(e) => setNewBotSettings(prev => ({ ...prev, useMinuteDelay: e.target.checked }))}
                  style={{ width: '18px', height: '18px' }}
                />
                <span style={{ fontWeight: '600' }}>⏰ Dakika bazlı gecikme kullan (meşgul botlar için)</span>
              </label>

              {newBotSettings.useMinuteDelay ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Min Gecikme (dakika)</label>
                    <input
                      type="number"
                      value={newBotSettings.minReplyDelayMinutes}
                      onChange={(e) => setNewBotSettings(prev => ({ ...prev, minReplyDelayMinutes: parseInt(e.target.value) }))}
                      min="1"
                      style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Max Gecikme (dakika)</label>
                    <input
                      type="number"
                      value={newBotSettings.maxReplyDelayMinutes}
                      onChange={(e) => setNewBotSettings(prev => ({ ...prev, maxReplyDelayMinutes: parseInt(e.target.value) }))}
                      min="1"
                      style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Min Gecikme (saniye)</label>
                    <input
                      type="number"
                      value={newBotSettings.minReplyDelaySeconds}
                      onChange={(e) => setNewBotSettings(prev => ({ ...prev, minReplyDelaySeconds: parseInt(e.target.value) }))}
                      min="1"
                      style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Max Gecikme (saniye)</label>
                    <input
                      type="number"
                      value={newBotSettings.maxReplyDelaySeconds}
                      onChange={(e) => setNewBotSettings(prev => ({ ...prev, maxReplyDelaySeconds: parseInt(e.target.value) }))}
                      min="1"
                      style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={addChatbotUser}
              disabled={addingBot}
              style={{
                width: '100%',
                padding: '1rem',
                backgroundColor: addingBot ? '#9ca3af' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: addingBot ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '1rem'
              }}
            >
              {addingBot ? '⏳ Ekleniyor...' : '✅ Chatbot Ekle'}
            </button>
          </div>
        )}

        {/* Bot Listesi */}
        {chatbotUsers.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#666', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            Henüz chatbot kullanıcısı eklenmemiş
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {chatbotUsers.map((bot) => (
              <div
                key={bot.id}
                style={{
                  padding: '1.5rem',
                  backgroundColor: bot.enabled ? '#f0fdf4' : '#fef2f2',
                  borderRadius: '12px',
                  border: `2px solid ${bot.enabled ? '#86efac' : '#fca5a5'}`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: bot.enabled ? '#22c55e' : '#ef4444'
                      }} />
                      <h4 style={{ margin: 0, fontSize: '1.2rem' }}>
                        {bot.persona?.name || bot.botName}
                        <span style={{ fontSize: '0.85rem', fontWeight: 'normal', color: '#666', marginLeft: '0.5rem' }}>
                          ({bot.persona?.age || '?'} yaş)
                        </span>
                      </h4>
                    </div>
                    <p style={{ margin: '0.25rem 0 0 1.5rem', fontSize: '0.85rem', color: '#666' }}>
                      {bot.botEmail} • {bot.persona?.currentCity || 'Belirtilmedi'}
                    </p>
                    <p style={{ margin: '0.25rem 0 0 1.5rem', fontSize: '0.85rem', color: '#666' }}>
                      {bot.persona?.profession || 'Meslek belirtilmedi'} • {bot.useMinuteDelay ? `${bot.minReplyDelayMinutes}-${bot.maxReplyDelayMinutes} dk gecikme` : `${bot.minReplyDelaySeconds}-${bot.maxReplyDelaySeconds} sn gecikme`}
                      {bot.maxUserMessages > 0 ? ` • ${bot.maxUserMessages} mesaj limiti` : ' • Sınırsız mesaj'}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => loadBotMessages(bot.botUserId, bot.botName)}
                      disabled={loadingMessages && viewingMessages === bot.botUserId}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: viewingMessages === bot.botUserId ? '#6366f1' : '#06b6d4',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      {loadingMessages && viewingMessages === bot.botUserId ? '⏳' : '💬'} Mesajlar
                    </button>
                    <button
                      onClick={() => scanChatbotMessages(bot.id, bot.botName, false)}
                      disabled={scanningBot === bot.id || !bot.enabled}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: scanningBot === bot.id ? '#9ca3af' : (!bot.enabled ? '#d1d5db' : '#8b5cf6'),
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: (scanningBot === bot.id || !bot.enabled) ? 'not-allowed' : 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      {scanningBot === bot.id ? '🔍 Taranıyor...' : '🔍 Tara'}
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`${bot.botName} için tüm eski kuyruk silinecek ve yeniden taranacak. Emin misiniz?`)) {
                          scanChatbotMessages(bot.id, bot.botName, true);
                        }
                      }}
                      disabled={scanningBot === bot.id || !bot.enabled}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: scanningBot === bot.id ? '#9ca3af' : (!bot.enabled ? '#d1d5db' : '#f97316'),
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: (scanningBot === bot.id || !bot.enabled) ? 'not-allowed' : 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      🔄 Zorla Tara
                    </button>
                    <button
                      onClick={() => setEditingBot(editingBot?.id === bot.id ? null : bot)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      ✏️ Düzenle
                    </button>
                    <button
                      onClick={() => toggleChatbotUser(bot.id, bot.enabled)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: bot.enabled ? '#f59e0b' : '#22c55e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      {bot.enabled ? '⏸️ Durdur' : '▶️ Başlat'}
                    </button>
                    <button
                      onClick={() => removeChatbotUser(bot.id, bot.botName)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      🗑️ Sil
                    </button>
                  </div>
                </div>

                {/* Mesaj Geçmişi */}
                {viewingMessages === bot.botUserId && (
                  <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'white', borderRadius: '8px', maxHeight: '400px', overflowY: 'auto' }}>
                    <h5 style={{ margin: '0 0 1rem 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>💬 {bot.botName} - Son Mesajlar ({botMessages.length})</span>
                      <button
                        onClick={() => loadBotMessages(bot.botUserId, bot.botName)}
                        style={{ padding: '0.25rem 0.5rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}
                      >
                        🔄 Yenile
                      </button>
                    </h5>
                    {botMessages.length === 0 ? (
                      <p style={{ color: '#666', textAlign: 'center', padding: '1rem' }}>Henüz mesaj yok</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {botMessages.map((msg) => (
                          <div key={msg.id} style={{ 
                            padding: '0.75rem', 
                            backgroundColor: '#f3f4f6', 
                            borderRadius: '8px',
                            borderLeft: '4px solid #10b981'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                              <span style={{ fontSize: '0.75rem', color: '#666' }}>
                                {msg.type === 'delayed' ? '⏰ Gecikmeli' : msg.type === 'greeting' ? '👋 Selam' : '💬 Cevap'}
                              </span>
                              <span style={{ fontSize: '0.75rem', color: '#999' }}>
                                {msg.createdAt?.toLocaleString?.('tr-TR') || '-'}
                              </span>
                            </div>
                            <div style={{ 
                              backgroundColor: '#dcfce7', 
                              padding: '0.5rem 0.75rem', 
                              borderRadius: '12px',
                              display: 'inline-block',
                              maxWidth: '80%'
                            }}>
                              <strong style={{ color: '#166534' }}>{msg.response || 'Cevap yok'}</strong>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Düzenleme Formu */}
                {editingBot?.id === bot.id && (
                  <div style={{ marginTop: '1rem', padding: '1.5rem', backgroundColor: 'white', borderRadius: '12px', border: '2px solid #3b82f6' }}>
                    <h5 style={{ margin: '0 0 1.5rem 0', fontSize: '1.1rem', color: '#1e40af' }}>✏️ Bot Ayarları: {bot.botName}</h5>
                    
                    {/* Persona Bilgileri */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h6 style={{ margin: '0 0 0.75rem 0', color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.5rem' }}>👤 Persona Bilgileri</h6>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', fontWeight: '600' }}>İsim</label>
                          <input
                            type="text"
                            value={editingBot.persona?.name || ''}
                            onChange={(e) => setEditingBot(prev => prev ? { ...prev, persona: { ...prev.persona, name: e.target.value } } : null)}
                            style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', fontWeight: '600' }}>Yaş</label>
                          <input
                            type="number"
                            value={editingBot.persona?.age || 25}
                            onChange={(e) => setEditingBot(prev => prev ? { ...prev, persona: { ...prev.persona, age: parseInt(e.target.value) } } : null)}
                            style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', fontWeight: '600' }}>Doğum Yeri</label>
                          <input
                            type="text"
                            value={editingBot.persona?.birthPlace || ''}
                            onChange={(e) => setEditingBot(prev => prev ? { ...prev, persona: { ...prev.persona, birthPlace: e.target.value } } : null)}
                            style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', fontWeight: '600' }}>Yaşadığı Şehir</label>
                          <input
                            type="text"
                            value={editingBot.persona?.currentCity || ''}
                            onChange={(e) => setEditingBot(prev => prev ? { ...prev, persona: { ...prev.persona, currentCity: e.target.value } } : null)}
                            style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', fontWeight: '600' }}>Meslek</label>
                          <input
                            type="text"
                            value={editingBot.persona?.profession || ''}
                            onChange={(e) => setEditingBot(prev => prev ? { ...prev, persona: { ...prev.persona, profession: e.target.value } } : null)}
                            style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', fontWeight: '600' }}>İş Durumu</label>
                          <input
                            type="text"
                            value={editingBot.persona?.jobStatus || ''}
                            onChange={(e) => setEditingBot(prev => prev ? { ...prev, persona: { ...prev.persona, jobStatus: e.target.value } } : null)}
                            style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', fontWeight: '600' }}>Kiminle Yaşıyor</label>
                          <input
                            type="text"
                            value={editingBot.persona?.livingWith || ''}
                            onChange={(e) => setEditingBot(prev => prev ? { ...prev, persona: { ...prev.persona, livingWith: e.target.value } } : null)}
                            style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', fontWeight: '600' }}>Medeni Durum</label>
                          <input
                            type="text"
                            value={editingBot.persona?.maritalStatus || ''}
                            onChange={(e) => setEditingBot(prev => prev ? { ...prev, persona: { ...prev.persona, maritalStatus: e.target.value } } : null)}
                            style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', fontWeight: '600' }}>Mesaj Limiti (0=sınırsız)</label>
                          <input
                            type="number"
                            value={editingBot.maxUserMessages}
                            onChange={(e) => setEditingBot(prev => prev ? { ...prev, maxUserMessages: parseInt(e.target.value) } : null)}
                            min="0"
                            style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Mesaj Ayarları */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h6 style={{ margin: '0 0 0.75rem 0', color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.5rem' }}>💬 Mesaj Ayarları</h6>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', fontWeight: '600' }}>
                            Takip Mesajı (kullanıcı sustugunda)
                          </label>
                          <input
                            type="text"
                            value={editingBot.followupMessage || ''}
                            onChange={(e) => setEditingBot(prev => prev ? { ...prev, followupMessage: e.target.value } : null)}
                            placeholder="ne oldu neden sustun"
                            style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                          />
                          <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Bos birakirsan varsayilan: "ne oldu neden sustun"</span>
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', fontWeight: '600' }}>
                            Veda Mesajı (mesaj limiti dolunca)
                          </label>
                          <input
                            type="text"
                            value={editingBot.farewellMessage || ''}
                            onChange={(e) => setEditingBot(prev => prev ? { ...prev, farewellMessage: e.target.value } : null)}
                            placeholder="Şu an müsait değilim, sonra görüşürüz"
                            style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                          />
                          <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Mesaj limiti dolunca gönderilir</span>
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', fontWeight: '600' }}>Bağlam Mesaj Sayısı</label>
                          <input
                            type="number"
                            value={editingBot.contextMessages || 20}
                            onChange={(e) => setEditingBot(prev => prev ? { ...prev, contextMessages: parseInt(e.target.value) } : null)}
                            min="5"
                            max="50"
                            style={{ width: '100%', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box' }}
                          />
                          <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>AI'nin hatırlayacağı son mesaj sayısı</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Gecikme Ayarları */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h6 style={{ margin: '0 0 0.75rem 0', color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.5rem' }}>⏰ Gecikme Ayarları</h6>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={editingBot.useMinuteDelay}
                            onChange={(e) => setEditingBot(prev => prev ? { ...prev, useMinuteDelay: e.target.checked } : null)}
                            style={{ width: '18px', height: '18px' }}
                          />
                          <span style={{ fontWeight: '600' }}>Dakika bazlı gecikme</span>
                        </label>
                        
                        {editingBot.useMinuteDelay ? (
                          <>
                            <input
                              type="number"
                              value={editingBot.minReplyDelayMinutes}
                              onChange={(e) => setEditingBot(prev => prev ? { ...prev, minReplyDelayMinutes: parseInt(e.target.value) } : null)}
                              style={{ width: '80px', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px' }}
                            />
                            <span>-</span>
                            <input
                              type="number"
                              value={editingBot.maxReplyDelayMinutes}
                              onChange={(e) => setEditingBot(prev => prev ? { ...prev, maxReplyDelayMinutes: parseInt(e.target.value) } : null)}
                              style={{ width: '80px', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px' }}
                            />
                            <span style={{ fontWeight: '600' }}>dakika</span>
                          </>
                        ) : (
                          <>
                            <input
                              type="number"
                              value={editingBot.minReplyDelaySeconds}
                              onChange={(e) => setEditingBot(prev => prev ? { ...prev, minReplyDelaySeconds: parseInt(e.target.value) } : null)}
                              style={{ width: '80px', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px' }}
                            />
                            <span>-</span>
                            <input
                              type="number"
                              value={editingBot.maxReplyDelaySeconds}
                              onChange={(e) => setEditingBot(prev => prev ? { ...prev, maxReplyDelaySeconds: parseInt(e.target.value) } : null)}
                              style={{ width: '80px', padding: '0.5rem', border: '2px solid #e5e7eb', borderRadius: '6px' }}
                            />
                            <span style={{ fontWeight: '600' }}>saniye</span>
                          </>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={updateChatbotUser}
                        style={{
                          padding: '0.75rem 2rem',
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '1rem'
                        }}
                      >
                        💾 Kaydet
                      </button>
                      <button
                        onClick={() => setEditingBot(null)}
                        style={{
                          padding: '0.75rem 1.5rem',
                          backgroundColor: '#6b7280',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer'
                        }}
                      >
                        İptal
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatbotPage;
